import { useEffect, useState } from 'react';
import { useStepContext } from '../contexts/stepContext';
import { useSubscriptionContext } from '../contexts/subscriptionContext';
import { useAppSelector, useTrack, useLocaleDiscountFormat } from '../hooks';
import { requestApplyRetentionDiscount, notify } from '../smi-bridge';
import { LocaleText } from '../LocaleText';
import { Footer } from '../components/Footer';
import { DiscountIcon } from '../components/icons';
import type { RetentionDiscountNode } from '../types';

export const RetentionDiscountStep = ({
  // step unused for now, but declared for consistency with other steps
  step: _step
}: {
  step: RetentionDiscountNode;
}) => {
  const { dispatch } = useStepContext();
  const subscription = useSubscriptionContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const track = useTrack();

  // Get retention incentive from state
  // we should always have this because of the eligibility logic in the reducer
  const retentionIncentive = useAppSelector(state => state.retention_incentives?.default_retention_incentive)!;

  // Get order item for this subscription
  const orderItem = useAppSelector(state => state.order_items.find(oi => oi.subscription === subscription.public_id));

  const formatDiscount = useLocaleDiscountFormat();

  // Format new discount for display
  const newDiscountText = formatDiscount(retentionIncentive.value, retentionIncentive.discount_type);

  // Track shown event on mount
  useEffect(() => {
    track('subscriptioncancel_discount_shown', {
      subscriptionId: subscription.public_id,
      incentiveTemplateId: retentionIncentive?.public_id
    });
  }, [track, subscription.public_id, retentionIncentive?.public_id]);

  async function handleApplyDiscount() {
    if (!orderItem || !retentionIncentive) {
      console.error('Cannot apply discount: missing order item or incentive');
      return;
    }

    try {
      setIsSubmitting(true);
      await requestApplyRetentionDiscount({
        target: 'item',
        target_id: orderItem.public_id,
        incentive_template_id: retentionIncentive.public_id
      });

      track('subscriptioncancel_completed_saved_discountclaimed', {
        subscriptionId: subscription.public_id,
        incentiveTemplateId: retentionIncentive.public_id
      });

      dispatch({ type: 'accepted' });
    } catch (error) {
      console.error('Error applying retention discount:', error);
      notify('An error occurred while applying your discount. Please try again later.', { type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="og-retention-discount-step">
      <div className="og-dialog-body">
        <p className="og-retention-discount-description">
          <LocaleText t="description" />
        </p>

        <div className="og-retention-discount">
          <DiscountIcon />
          <div className="og-retention-discount-content">
            <h2 className="og-retention-discount-heading">
              <LocaleText t="discountHeading" parameters={{ discount: newDiscountText }} />
            </h2>
            <p className="og-retention-discount-subheading">
              <LocaleText t="discountSubheading" />
            </p>
          </div>

          <button
            className="og-button og-retention-discount-cta"
            data-variant="primary"
            disabled={isSubmitting}
            onClick={handleApplyDiscount}
          >
            <LocaleText t="acceptButton" />
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
};
